import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';

@customElement('luna-control-toggle')
export class LunaControlToggle extends LitPage {
  @property({ attribute: 'label' }) label = '';
  @property({ attribute: 'hint' }) hint = '';
  @property({ type: Boolean }) value = false;

  private toggle(): void {
    this.value = !this.value;
    this.dispatchEvent(
      new CustomEvent('luna-change', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  content() {
    return html`
      <div class="luna-control-toggle">
        <div class="luna-control-toggle__row">
          <span class="luna-control-toggle__label">${this.label}</span>
          <button
            type="button"
            class="luna-control-toggle__switch ${this.value ? 'luna-control-toggle__switch--on' : ''}"
            role="switch"
            aria-checked=${this.value ? 'true' : 'false'}
            aria-label=${this.label}
            @click=${this.toggle}
          >
            <span class="luna-control-toggle__track" aria-hidden="true">
              <span class="luna-control-toggle__knob"></span>
            </span>
          </button>
        </div>
        ${this.hint
          ? html`<span class="luna-control-toggle__hint">${this.hint}</span>`
          : ''}
      </div>
    `;
  }

  styles() {
    return css`
      .luna-control-toggle {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 420px;
        max-width: 100%;
      }

      .luna-control-toggle__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        height: 56px;
      }

      .luna-control-toggle__label {
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 15px; /* pinned: figma Luna Stepper (no Bold-15 type style) */
        line-height: 20px;
        color: var(--cds-text-primary);
      }

      .luna-control-toggle__switch {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 44px;
        height: 44px; /* ≥44×44 touch target (BR-71); track is 44×24 inside */
        border: none;
        background: none;
        cursor: pointer;
      }

      .luna-control-toggle__switch:focus-visible {
        outline: none;
        box-shadow:
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-control-toggle__track {
        position: relative;
        width: 44px;
        height: 24px;
        border-radius: var(--luna-radius-pill);
        background: var(--luna-hatch-border);
        transition: background var(--mbw-motion-dur-fast);
      }

      .luna-control-toggle__switch--on .luna-control-toggle__track {
        background: var(--luna-outline-checkpoint);
      }

      .luna-control-toggle__knob {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #ffffff; /* g10-contrast knob */
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        transition: transform var(--mbw-motion-dur-fast);
      }

      .luna-control-toggle__switch--on .luna-control-toggle__knob {
        transform: translateX(20px);
      }

      .luna-control-toggle__hint {
        font-family: var(--mbw-font-sans);
        font-weight: 400;
        font-size: 13px; /* pinned: figma Luna Stepper (no 13px type style) */
        line-height: 17px;
        color: var(--mbw-text-faint);
      }
    `;
  }
}
